import { apiGet, apiPost } from '../utils/api';

/**
 * Grounding Service Class
 * Handles all grounding-related API calls and data management
 */
class GroundingService {
  constructor() {
    this.baseUrl = '/.netlify/functions';
  }

  // Note: Token is now automatically retrieved from storage via api.js

  /**
   * Assign grounding technique to patient (Therapist only)
   * @param {Object} user - Current user
   * @param {string} patientId - Patient ID
   * @param {Object} techniqueConfig - Technique configuration
   * @returns {Promise<Object>} Assignment result
   */
  async assignTechnique(user, patientId, techniqueConfig) {
    const response = await apiPost(
      `${this.baseUrl}/assign-grounding-technique`,
      {
        patientId,
        techniqueConfig
      }
    );

    return response;
  }

  /**
   * Get grounding assignment
   * @param {Object} user - Current user
   * @param {string} patientId - Optional patient ID (for therapists)
   * @returns {Promise<Object>} Assignment data
   */
  async getAssignment(user, patientId = null) {
    const params = patientId ? `?patientId=${patientId}` : '';
    const response = await apiGet(
      `${this.baseUrl}/get-grounding-assignment${params}`
    );

    return response;
  }

  /**
   * Start a grounding session
   * @param {Object} user - Current user
   * @param {string} groundingId - Grounding assignment ID
   * @param {Object} sessionData - Optional session data
   * @returns {Promise<Object>} Session data
   */
  async startSession(user, groundingId, sessionData = {}) {
    const response = await apiPost(
      `${this.baseUrl}/start-grounding-session`,
      {
        groundingId,
        sessionData
      }
    );

    return response;
  }

  /**
   * Complete a grounding session
   * @param {Object} user - Current user
   * @param {string} sessionId - Session ID
   * @param {Object} completionData - Completion data
   * @returns {Promise<Object>} Completed session data
   */
  async completeSession(user, sessionId, completionData) {
    const response = await apiPost(
      `${this.baseUrl}/complete-grounding-session`,
      {
        sessionId,
        completionData
      }
    );

    return response;
  }

  /**
   * Get grounding analytics
   * @param {Object} user - Current user
   * @param {string} patientId - Optional patient ID (for therapists)
   * @param {string} timeRange - Time range (week, month, quarter)
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics(user, patientId = null, timeRange = 'month') {
    let params = `?timeRange=${timeRange}`;
    if (patientId) {
      params += `&patientId=${patientId}`;
    }
    
    const response = await apiGet(
      `${this.baseUrl}/get-grounding-analytics${params}`
    );

    return response;
  }

  /**
   * Validate technique configuration
   * @param {Object} config - Technique configuration
   * @returns {boolean} Is valid
   */
  validateTechniqueConfig(config) {
    if (!config.techniqueType) {
      throw new Error('Technique type is required');
    }

    const validTypes = ['breathing', 'thoughts-facts'];
    if (!validTypes.includes(config.techniqueType)) {
      throw new Error('Invalid technique type');
    }

    // Only validate breathing-specific fields for breathing exercises
    if (config.techniqueType === 'breathing') {
      const required = ['inhaleDuration', 'holdDuration', 'exhaleDuration', 'cycles'];
      
      for (const field of required) {
        if (!config[field]) {
          throw new Error(`${field} is required for breathing exercises`);
        }
      }

      if (config.cycles > 20) {
        throw new Error('Maximum 20 cycles allowed');
      }

      if (config.inhaleDuration > 60 || config.holdDuration > 60 || config.exhaleDuration > 60) {
        throw new Error('Maximum 60 seconds per phase allowed');
      }
    }

    return true;
  }

  /**
   * Get default breathing technique configuration
   * @returns {Object} Default configuration
   */
  getDefaultBreathingConfig() {
    return {
      techniqueType: 'breathing',
      inhaleDuration: 10,
      holdDuration: 5,
      exhaleDuration: 10,
      cycles: 3,
      instructions: 'Focus on your breath and let go of tension'
    };
  }

  /**
   * Format date for display in user's local timezone
   * @param {string|Date} dateInput - Date string or Date object
   * @returns {string} Formatted date string
   */
  formatDateForDisplay(dateInput) {
    if (!dateInput) return 'N/A';
    
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }

  /**
   * Get local date string from UTC timestamp
   * @param {string|Date} utcTimestamp - UTC timestamp
   * @returns {string} Local date string (YYYY-MM-DD)
   */
  getLocalDateString(utcTimestamp) {
    if (!utcTimestamp) return '';
    
    try {
      const date = new Date(utcTimestamp);
      if (isNaN(date.getTime())) return '';
      
      // Get local date components
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error getting local date string:', error);
      return '';
    }
  }

  /**
   * Regroup analytics data by local timezone dates
   * @param {Array} usageData - Usage data from backend
   * @param {Array} sessions - Raw session data
   * @returns {Array} Regrouped usage data by local dates
   */
  regroupByLocalDates(usageData, sessions) {
    const localUsageMap = {};
    
    // Process each session and group by local date
    sessions.forEach(session => {
      const localDateStr = this.getLocalDateString(session.createdAt);
      if (!localDateStr) return;
      
      if (!localUsageMap[localDateStr]) {
        localUsageMap[localDateStr] = {
          date: localDateStr,
          createdAt: session.createdAt,
          sessions: 0,
          duration: 0,
          latestSession: session.createdAt
        };
      }
      
      localUsageMap[localDateStr].sessions++;
      localUsageMap[localDateStr].duration += session.duration || 0;
      
      // Keep the latest session timestamp for this date
      if (session.createdAt > localUsageMap[localDateStr].latestSession) {
        localUsageMap[localDateStr].latestSession = session.createdAt;
      }
    });
    
    return Object.values(localUsageMap).sort((a, b) => b.date.localeCompare(a.date));
  }
}

// Create singleton instance
const groundingService = new GroundingService();

export default groundingService;
